import { getUID } from '@redviking/argonaut-util/src/getUID';
import { UiFlowNodeControl } from '../../controls/UiFlowNodeControl';
import { UiFlowNodeOutput } from '../../sockets/UiFlowNodeOutput';
import { removeNodeSocket } from '../../sync-utils/remove-node-socket';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import { accessor } from '@redviking/argonaut-core-ui/src/store';
import { latestCalendarColumnLabels, latestCalendarColumnSelectionMap } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import type { LatestCalendarUdfColumn, NodeCfg, NodeSocketKind, NodeSocketKindMap } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { ArgoUdfColumnDataTypeEnum } from 'types/db';
import type { UiNodeSpec } from './UiNodeSpec';

export const calendarUiNodeSpec: UiNodeSpec<'calendar'> = {
    requiredSockets: (node, _, availableOutputSockets) => ({
        required: [],
        conditionallyRequired: [
            {
                kind: 'or',
                availableOptions: availableOutputSockets,
            },
        ],
    }),
    inputs: () => ({}),
    kindLabel: 'Calendar',
    nodeCategory: 'argonautApi',
    suppressNode: () => Boolean(accessor.featureFlags.unstableCalendarNode && accessor.featureFlags.schedules),
    controls: getNodeCfg => ({
        location: {
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.location,
            cfg: {
                kind: 'location',
            },
        },
        subcribedColumns: {
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.subcribedColumns,
            cfg: {
                selectLabel: 'Add Output',
                kind: 'selectWithCallback',
                items: () => {
                    const nodeCfg = getNodeCfg();
                    const notUsedOutputs = Object.entries(nodeCfg.controlValues.subcribedColumns).filter(([ , value ]) => !value).map(([ key ]) => key);
                    return latestCalendarColumnSelectionMap.filter(item => notUsedOutputs.includes(item.value));
                },
                onSelect (value) {
                    const nodeCfg = getNodeCfg();
                    const typedValue = value as keyof typeof nodeCfg.controlValues.subcribedColumns;
                    createNodeSocket({
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                subcribedColumns: {
                                    ...nodeCfg.controlValues.subcribedColumns,
                                    [typedValue]: true,
                                },
                            },
                        },
                    });
                },
            },
        },
        udfColumns: {
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.udfColumns,
            cfg: {
                kind: 'selectCalendarUdfOutput',
                onSelect (value) {
                    const {
                        udfId,
                        udfData,
                    } = value as {
                        udfId: string;
                        udfData: LatestCalendarUdfColumn;
                    };
                    const nodeCfg = getNodeCfg();
                    createNodeSocket({
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                udfColumns: {
                                    ...nodeCfg.controlValues.udfColumns,
                                    [udfId]: udfData,
                                },
                            },
                        },
                    });
                },
            },
        },
    }),
    outputs: nodeCfg => {
        const outputs: {
            [outputKey in keyof NodeSocketKindMap['calendar']['outputs']]: UiFlowNodeOutput.Opts<NodeSocketKindMap['calendar']['outputs'][outputKey] & NodeSocketKind>;
        } = {
            event: { socket: ('event'), label: 'On Update' },
        };

        for (const [ key, value ] of Object.entries(nodeCfg.controlValues.subcribedColumns)) {
            const typedKey = key as keyof typeof latestCalendarColumnLabels;
            if (value) {
                const control: UiFlowNodeControl.Opts<string> = {
                    nodeCfg,
                    value: '',
                    cfg: {
                        rightAlign: true,
                        kind: 'deletableSocket',
                        label: latestCalendarColumnLabels[typedKey],
                        onDelete () {
                            removeNodeSocket<'calendar'>({
                                direction: 'output',
                                removalMethod: {
                                    kind: 'uniqueIdentifier',
                                    value: typedKey,
                                },
                                newNodeCfg: {
                                    ...nodeCfg,
                                    controlValues: {
                                        ...nodeCfg.controlValues,
                                        subcribedColumns: {
                                            ...nodeCfg.controlValues.subcribedColumns,
                                            [typedKey]: false,
                                        },
                                    },
                                },
                            });
                        },
                    },
                };
                outputs[typedKey] = ({
                    control,
                    socket: 'string',
                });
                if ([
                    'shiftCurrMetaName',
                    'shiftNextMetaName',
                    'nonProdNextMetaName',
                    'nonProdCurrMetaName',
                    'shiftCurrMetaTimeEnd',
                    'shiftNextMetaTimeEnd',
                    'nonProdNextMetaTimeEnd',
                    'nonProdCurrMetaTimeEnd',
                    'shiftNextMetaTimeStart',
                    'shiftCurrMetaTimeStart',
                    'nonProdCurrMetaTimeStart',
                    'nonProdNextMetaTimeStart',
                ].includes(typedKey)) {
                    outputs[typedKey] = {
                        control,
                        socket: 'string',
                    };
                } else {
                    outputs[typedKey] = {
                        control,
                        socket: 'number',
                    };
                }
                // else if (typedKey === 'rawCalenderUdfData') {
                //     // outputs[typedKey] = new UiFlowNodeOutput({
                //     //     control: control as UiFlowNodeControl<unknown>,
                //     //     socket: new UiFlowNodeSocket('workOrderMaterialData'),
                //     // });
                // }
            }
        }
        for (const [ udfColId, udfColData ] of Object.entries(nodeCfg.controlValues.udfColumns)) {
            const control = new UiFlowNodeControl({
                nodeCfg,
                value: '',
                cfg: {
                    rightAlign: true,
                    kind: 'deletableSocket',
                    label: `${udfColData.eventName} - ${udfColData.occurance === 'current' ? 'Current' : 'Next'} - ${udfColData.udfName}`,
                    onDelete () {
                        const newNodeUdfColumns = { ...nodeCfg.controlValues.udfColumns };
                        delete newNodeUdfColumns[udfColId];
                        removeNodeSocket<'calendar'>({
                            direction: 'output',
                            removalMethod: {
                                kind: 'uniqueIdentifier',
                                value: `udfColumns_${udfColId}`,
                            },
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    udfColumns: newNodeUdfColumns,
                                },
                            },
                        });
                    },
                },
            });
            outputs[`udfColumns_${udfColId}`] = {
                control,
                socket: udfColData.dataType === ArgoUdfColumnDataTypeEnum.Number ? 'number' : 'string',
            };
        }
        return outputs;
    },
    cfg: () => {
        const ret: NodeCfg<'calendar'> = {
            disabled: false,
            id: getUID(),
            kind: 'calendar',
            controlValues: {
                udfColumns: {},
                location: { id: '' },
                subcribedColumns: {
                    shiftNextMetaName: false,
                    shiftCurrMetaName: false,
                    // rawCalenderUdfData: false,
                    nonProdCurrMetaName: false,
                    nonProdNextMetaName: false,
                    shiftNextMetaTimeEnd: false,
                    shiftCurrMetaTimeEnd: false,
                    shiftNextMetaTimeStart: false,
                    shiftCurrMetaTimeStart: false,
                    nonProdCurrMetaTimeEnd: false,
                    nonProdNextMetaTimeEnd: false,
                    nonProdNextMetaTimeStart: false,
                    nonProdCurrMetaTimeStart: false,
                    shiftNextTimeSecondsUntil: false,
                    shiftCurrTimeElapsedSeconds: false,
                    nonProdNextTimeSecondsUntil: false,
                    shiftCurrTimeRemainingSeconds: false,
                    nonProdCurrTimeElapsedSeconds: false,
                    shiftCurrTimeElapsedProductive: false,
                    nonProdCurrTimeRemainingSeconds: false,
                    shiftCurrTimeRemainingProductive: false,
                },
            },
        };
        return ret;
    },
    validator: node => {
        const errors: Record<string, string> = {};

        if ('deployParamKind' in node.controlValues.location) {
            if (!node.controlValues.location.deployParamId) {
                errors.location = 'Location deployment parameter is required';
            }
        } else if (!node.controlValues.location.id) {
            errors.location = 'Location is required';
        }

        return Object.keys(errors).length ? errors : true;
    },
};
